import React, { useState, useEffect, useContext } from 'react'
import { Button } from 'react-bootstrap'

import globalNotification from 'live/services/global-notification'
import i18n from 'live/services/i18n'

import { FilteredSearch, RemoveConfirmationModal, Scrollbars } from '../../../generic'
import { CustomList, IDirection } from '../../../generic/CustomList/CustomList'
import { useDimensions } from '../../../hooks'
import { ResourceTypeConfigurationRouteContext } from '../../../ResourceGroupConfiguration/ResourceGroupConfigurationWrapper'
import { IResourceManagerRouteContext, IQueryParamsList } from '../../../types'
import { ResourceGroup, deleteResourceGroup, getResourceGroups } from '../../../utils/services/resourceGroup'
import { listUser } from '../../../utils/services/users'
import { NewGroupScreen } from './components'
import { ResourceGroupListContainer } from './styles'

export const ResourceGroupList: React.FC = () => {
    const dimensions = useDimensions()

    const [loading, setLoading] = useState<boolean>(false)
    const [resourceGroups, setResourceGroups] = useState<ResourceGroup[]>()
    const [tempResourceGroup, setTempResourceGroup] = useState<ResourceGroup>()
    const [searchTerm, setSearchTerm] = useState<string>('')
    const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false)
    const [mode, setMode] = useState<string>('list')
    const [resourceGroupToEdit, setResourceGroupToEdit] = useState<ResourceGroup>()
    const [users, setUsers] = useState<LiveUser[]>()

    const { params, setParams } = useContext<IResourceManagerRouteContext<IQueryParamsList>>(
        ResourceTypeConfigurationRouteContext)
    const [sortDirection, setSortDirection] = useState<'ASC' | 'DESC'>('ASC')
    // const [sortField, setSortField] = useState<keyof ResourceGroup>('name')

    const handleSort = (newSortBy: keyof ResourceGroup, direction: IDirection): void => {
        setParams({ ...params, orderBy: newSortBy.toUpperCase(), direction: direction })
        // setSortField(newSortBy)
        setSortDirection(direction)
    }

    const handleGetResourceGroups = async (): Promise<void> => {
        try {
            setLoading(true)
            const data = await getResourceGroups(params)
            setResourceGroups(data)
        } catch (error) {
            globalNotification.error(error.message ?? i18n('Unable to fetch resource groups.'))
        } finally {
            setLoading(false)
        }
    }

    const handleGetLiveUsers = async (): Promise<void> => {
        try {
            setLoading(true)
            const data = await listUser()
            setUsers(data)
        } catch (error) {
            globalNotification.error(error.message ?? i18n('Unable to fetch live users.'))
        } finally {
            setLoading(false)
        }
    }

    const generateTempOperationArray = (): ResourceGroup[] => {
        return resourceGroups.filter(resourceGroup => resourceGroup.name.toLowerCase().includes(searchTerm))
    }

    const handleShowEditScreen = (resourceGroup: ResourceGroup): void => {
        setTempResourceGroup(resourceGroup)
        setResourceGroupToEdit(resourceGroup)
        setMode('edit')
    }

    const handleShowRemoveModal = (resourceGroup: ResourceGroup): void => {
        setShowDeleteModal(true)
        setTempResourceGroup(resourceGroup)
    }

    const handleCloseProtocol = (): void => {
        setShowDeleteModal(false)
        setTempResourceGroup(null)
    }

    useEffect(() => {
        handleGetResourceGroups()
        handleGetLiveUsers()
    }, [mode, params])

    return (
        <Scrollbars height={dimensions.height}>
            {mode === 'list' && (
                <ResourceGroupListContainer className="container">
                    <div className="first-row-container">
                        <Button
                            className="no-outline-button"
                            bsStyle="primary"
                            onClick={() => setMode('create')}
                            style={{ borderRadius: 3, marginRight: 8 }}
                            id="diagramList-list-button-newDiagram"
                        >
                            <i className="far fa-plus custom-plus" /> {i18n('New Resource Group')}
                        </Button>

                        <FilteredSearch inputId="diagramList-list-field-searchDiagram" onSearch={setSearchTerm} />
                    </div>
                    {loading ? (
                        <div className="loading-spin">
                            <i className="fa fa-spinner fa-spin fa-x2" />
                        </div>
                    ) : resourceGroups?.length ? (
                        <CustomList
                            label={i18n('Resource Groups')}
                            list={resourceGroups
                                .filter(resourceGroup =>
                                    resourceGroup.name.toLowerCase().includes(searchTerm.toLowerCase())
                                )
                                .map(el => ({
                                    ...el,
                                    assetTypeDisplay: (
                                        <div className="types">
                                            {el.assetTypes.map((elm, index) => (
                                                <span key={index}>{elm.name}</span>
                                            ))}
                                        </div>
                                    ),
                                    liveUserIdsDisplay: (
                                        <div className="users">
                                            {el.liveUserIds.map((elm, index) => (
                                                <span key={index}>
                                                    {users?.filter(el => el.id === elm).map(name => name.name)}
                                                </span>
                                            ))}
                                        </div>
                                    )
                                }))}
                            columns={[
                                { title: 'NAME', sortable: true, listRef: 'name' },
                                { title: 'RESOURCE TYPES', sortable: false, listRef: 'assetTypeDisplay' },
                                { title: 'users', sortable: false, listRef: 'liveUserIdsDisplay' }
                            ]}
                            sortDirection={sortDirection}
                            sortField={'name'}
                            onSortClick={handleSort}
                            tableProps={{ style: { width: '100%' } }}
                            configOptions={[
                                { icon: 'far fa-edit', onClickConfig: o => handleShowEditScreen(o) },

                                { icon: 'far fa-trash-alt', onClickConfig: o => handleShowRemoveModal(o) }
                            ]}
                            pagination={{
                                type: 'live',
                                totalItems: generateTempOperationArray().length,
                                showingItems: generateTempOperationArray().length,
                                perPage: 30
                            }}
                        />
                    ) : (
                        <div className="no-asset-group">
                            <i className="far fa-folder-open " />
                            <p>{i18n('No groups have been added.')}</p>
                        </div>
                    )}
                    <RemoveConfirmationModal
                        setShow={setShowDeleteModal}
                        show={showDeleteModal}
                        handleClose={handleCloseProtocol}
                        type={i18n('resource group')}
                        message={i18n('If you really want to delete the resource group, type')}
                        confirmationMessage={i18n('Resource group deleted succesfully.')}
                        removalMessage={`${i18n('Are you sure you want remove the resource group')} ${
                            tempResourceGroup?.name
                        }?`}
                        onDelete={async () => {
                            await deleteResourceGroup(tempResourceGroup.id)
                            handleGetResourceGroups()
                        }}
                    />
                </ResourceGroupListContainer>
            )}
            {mode === 'create' && <NewGroupScreen mode={mode} setMode={setMode} />}
            {mode === 'edit' && <NewGroupScreen mode={mode} setMode={setMode} idToEdit={resourceGroupToEdit.id} />}
        </Scrollbars>
    )
}
